import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { definitionProblemText } from "../../../errors/definitionProblems";
import ErrorNotice from "../../../errors/ErrorNotice";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import FieldChangeList from "../../items/changes/FieldChangeList";
import { readableJsonText } from "../../../shared/formatting/readableJson";
import { fieldDiff } from "../../../shared/diff/fieldDiff";
import { definitionPath, type DefinitionCheck, type ShapesCheck } from "./useDefinitionCheck";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import Tabs from "../../../ui/navigation/Tabs";
import SourceView from "../../../ui/content/source/SourceView";

type PanelView = "file" | "changes";

interface DefinitionPanelProps {
  /** The definition the draft becomes. */
  content: unknown;
  /** The version the draft started from; `null` for a new item. */
  base: unknown;
  check: DefinitionCheck;
}

/**
 * Beside every form (AU-3): the stored file the draft becomes, its changes against the
 * version it started from, and the server's checks, so people see that a step is data.
 */
export default function DefinitionPanel({ content, base, check }: DefinitionPanelProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<PanelView>("file");
  const text = useMemo(() => readableJsonText(content), [content]);
  const changes = useMemo(() => (base === null ? null : fieldDiff(base, content)), [base, content]);
  const title = <h3 className="app-title flex-1 text-sm font-semibold">{t("authoring.definition.title")}</h3>;
  const file = <SourceView label={t("authoring.definition.file")} language="json" value={text} />;
  return (
    <aside className="space-y-3" aria-label={t("authoring.definition.label")}>
      <section className="app-card space-y-2 p-3">
        {changes === null ? (
          <>
            {title}
            {file}
          </>
        ) : (
          <Tabs
            label={t("authoring.definition.views")}
            options={[
              { key: "file", label: t("authoring.definition.file") },
              { key: "changes", label: t("authoring.definition.changes", { count: changes.length }) },
            ]}
            selected={view}
            onSelect={setView}
            header={title}
          >
            {view === "changes" ? <FieldChangeList changes={changes} /> : file}
          </Tabs>
        )}
      </section>
      <CheckSummary check={check} />
    </aside>
  );
}

/** The server's verdict on the draft: checking, the problems, or what the review's shapes say. */
export function CheckSummary({ check }: { check: DefinitionCheck }) {
  const { t } = useTranslation();
  return (
    <section className="app-card space-y-2 p-3" aria-live="polite">
      <h3 className="app-title text-sm font-semibold">{t("authoring.check.title")}</h3>
      {check.status === "checking" ? <LoadingIndicator label={t("authoring.check.checking")} /> : null}
      {check.status === "failed" ? <ErrorNotice error={check.error} compact={true} /> : null}
      {check.status === "checked" && check.problems.length === 0 ? <ShapesVerdict check={check.shapes} /> : null}
      {check.status === "checked" && check.problems.length > 0 ? (
        // The fields show their own problems; here the count and what it means, the words on request (PK-4+5 I2, I3).
        <StatusMessage tone="error" className="space-y-1 px-2 py-1 text-sm">
          <p>{t("authoring.check.problems", { count: check.problems.length })}</p>
          <details className="text-xs">
            <summary>{t("authoring.check.showProblems")}</summary>
            <ul className="mt-1 space-y-1">
              {check.problems.map((problem) => (
                <li key={`${problem.path} ${problem.message}`}>
                  {definitionProblemText(problem, t)} <span className="app-text-muted font-mono">{definitionPath(problem)}</span>
                </li>
              ))}
            </ul>
          </details>
        </StatusMessage>
      ) : null}
    </section>
  );
}

/**
 * What the review's shapes say about a draft without problems (reviews.md R-3): a violation
 * the draft brings would block the merge; one `main` already has is only counted.
 */
function ShapesVerdict({ check }: { check: ShapesCheck }) {
  const { t } = useTranslation();
  if (check.status === "checking") {
    return <LoadingIndicator label={t("authoring.check.shapesChecking")} />;
  }
  if (check.status === "failed") {
    return <ErrorNotice error={check.error} compact={true} />;
  }
  const shapes = check.shapes;
  if (shapes === null) {
    return <StatusMessage tone="success" className="px-2 py-1 text-sm">{t("authoring.check.passes")}</StatusMessage>;
  }
  const introduced = shapes.introduced.length + shapes.unlistedIntroduced;
  const preExisting = (
    <>
      {shapes.preExisting.length > 0 ? <p className="app-help-text text-xs">{t("authoring.check.preExisting", { count: shapes.preExisting.length })}</p> : null}
      {shapes.uncomparedCheckIds.length > 0 ? <p className="app-help-text text-xs">{t("authoring.check.uncompared", { count: shapes.uncomparedCheckIds.length })}</p> : null}
    </>
  );
  if (introduced === 0) {
    return (
      <>
        {/* Uncompared checks found nothing new only as far as they were compared: claim no more (Sol R2 S4). */}
        <StatusMessage tone="success" className="px-2 py-1 text-sm">{t(shapes.uncomparedCheckIds.length > 0 ? "authoring.check.passes" : "authoring.check.passesShapes")}</StatusMessage>
        {preExisting}
      </>
    );
  }
  return (
    <>
      <StatusMessage tone="error" className="px-2 py-1 text-sm">
        <p>{t("authoring.check.blocked", { count: introduced })}</p>
        <ul className="mt-1 space-y-1">
          {shapes.introduced.map((violation, index) => (
            <li key={`${violation.checkId} ${violation.finding.message} ${index}`}>
              {violation.checkTitle === null ? null : <span className="font-semibold">{violation.checkTitle}: </span>}
              {violation.finding.message}
            </li>
          ))}
        </ul>
      </StatusMessage>
      {preExisting}
    </>
  );
}
